import { Injectable } from '@angular/core';
import { NewsFeedService } from '@fi-app/news-feed/news-feed.service';
import { ApplicationSettingsType } from '@fi-shared/types/application-settings-type';
import { ModuleType } from '@fi-shared/types/module-type';
import { DocumentService } from '@fi-app/document/document.service';
import { SideListOptionType } from '@fi-shared/types/side-list-option-type';
import { MediaBankService } from '@fi-app/media-bank/media-bank.service';
import { SessionService } from '@fi-app/utilities/fi-services/session.service';
import { AppTextService } from '@fi-shared/app-text';
import { StateAccessCheckService } from '@fi-shared/fi-shared-services/state-access-check.service';
import { CustomButtonModel } from './custom-button.model';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { ChecklistsReminderService } from '@fi-app/system-notifications/checklists/checklists-reminder.service';
import { FiFormService } from '@fi-app/fi-form/fi-form.service';
import { Observable } from 'rxjs';
import { FiSignalRHelperService } from '@fi-shared/fi-signal-r/fi-signal-r-helper.service';
import { map } from 'rxjs/operators';
import { FiUserDataService, IFiUserModuleAccessRights } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { FiStateChangeService } from '@fi-shared/fi-state-proxy/fi-state-change.service';
import { FiNewsFeedSharedService } from '@fi-shared/fi-news-feed-shared/fi-news-feed-shared.service';
import { GlobalSettingsService } from '@fi-app/global-settings/global-settings.service';
import { FiTaskToCompleteTabsEnum } from '@fi-shared/fi-task-shared/fi-task-to-complete-tabs.enum';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { BalooService } from '@fi-app/external-platform-integration/baloo/baloo.service';
import { FiChecklist2SyncService } from '@fi-shared/checklist/fi-checklist2-sync.service';
import { ChecklistV2StatesService } from '@fi-app/checklist/checklist-v2/checklist-v2-states.service';
import { FiChecklistStatesService } from '@fi-app/checklist/fi-checklist-states.service';
import { FiTaskSyncService } from '@fi-shared/fi-task-shared/fi-task-sync-service';
import { FiTaskV2HelperService } from '@fi-app/task-v2/fi-task-v2-helper.service';
import { FiOnboardingHelperService } from '@fi-app/fi-onboarding/fi-onboarding-helper.service';
import { FiOnboardingNavigationService } from '@fi-app/fi-onboarding/fi-onboarding-navigation.service';

export interface IMenuItem {
    ApplicationSettingType: ApplicationSettingsType[];
    MinAccessRightType?;
    ExcludingSettingTypes?: any[];
    labelText: string;
    Icon: string;
    State?: string;
    StateParams?: any;
    Count?: () => number | void; // todo use count$
    CountParams?;
    CountState?: string;
    countMoreMenuSettings?: {
        menuItems: any[];
    };
    count$?: Observable<number>;
    RootStates?: string[];
    ModuleType?;
    getUrl?: () => string;
    onClick?: (menuItem: IMenuItem, $event: MouseEvent) => void;
    buttonModel?: CustomButtonModel;
    isHidden?: () => boolean;
}

@Injectable()
export class CoreService {
    private menuItemsMap: IMenuItem[];

    constructor(
        private newsFeedSharedService: FiNewsFeedSharedService,
        private documentService: DocumentService,
        private mediaBankService: MediaBankService,
        private formService: FiFormService,
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
        private checklistsReminderService: ChecklistsReminderService,
        private fiBackend: FiBackendService,
        private fiSignalRHelperService: FiSignalRHelperService,
        private stateService: FiStateService,
        private customerDataService: FiCustomerDataService,
        private userDataService: FiUserDataService,
        private taskSyncService: FiTaskSyncService,
        private balooService: BalooService,
        private fiStateChangeService: FiStateChangeService,
        private globalSettingsService: GlobalSettingsService,
        private fiChecklistStatesService: FiChecklistStatesService,
        private checklistV2StatesService: ChecklistV2StatesService,
        private fiChecklist2SyncService: FiChecklist2SyncService,
        private fiTaskV2HelperService: FiTaskV2HelperService,
        private fiOnboardingNavigationService: FiOnboardingNavigationService,
    ) {
        this.buildMenuItemsMap();
        //this.updateMenuItems(this.userDataService.userData.ModuleAccessRights);

        this.userDataService.onApplicationLanguageChange$.subscribe(() => {
            this.buildMenuItemsMap();
        });

        this.fiStateChangeService.onSuccess$.subscribe((tr) => {
            if (!this.userDataService.getUserId()) {
                return;
            }

            const from = tr.from();
            const to = tr.to();
            if (from.name === FiStateNames.Home._name) {
                this.markAllNewsFeedsAsSeen();
            }
            if (from.name !== FiStateNames.Document.List && to.name === FiStateNames.Document.List) {
                setTimeout(() => {
                    if (this.userDataService.getUserId()) {
                        this.markAllDocumentsAsSeen();
                    }
                }, 5000);
            } else if (from.name !== FiStateNames.MediaBank.List && to.name === FiStateNames.MediaBank.List) {
                setTimeout(() => {
                    if (this.userDataService.getUserId()) {
                        this.markAllMediaFilesAsSeen();
                    }
                }, 5000);
            }
        });
    }

    getMenuItems(rights: IFiUserModuleAccessRights) {
        if (!rights) {
            return [];
        }

        const menuItems: IMenuItem[] = [];

        const appendedSettingsTypes = [];

        // todo refactor for using isHidden for clarity
        this.menuItemsMap.forEach((mapItem) => {
            if (mapItem.ApplicationSettingType) {
                for (let y = 0; y < rights.AppSettingsRights.length; y++) {
                    if (
                        mapItem.ApplicationSettingType.indexOf(rights.AppSettingsRights[y].ApplicationSettingType) !==
                        -1
                    ) {
                        if (
                            mapItem.ExcludingSettingTypes &&
                            appendedSettingsTypes.filter((n) => mapItem.ExcludingSettingTypes.indexOf(n) > -1).length >
                                0
                        )
                            break;

                        if (
                            mapItem.MinAccessRightType &&
                            rights.AppSettingsRights[y].AccessRightType < mapItem.MinAccessRightType
                        ) {
                            break;
                        }
                        menuItems.push(mapItem);
                        mapItem.ApplicationSettingType.forEach((item) => appendedSettingsTypes.push(item));
                        break;
                    }
                }
            } else {
                // just skip ApplicationSettingType check
                menuItems.push(mapItem);
            }
        });

        this.customerDataService.customerData.CustomButtons.forEach((btn) => {
            if (rights.CustomButtons.indexOf(btn.Id) < 0) return;

            const customButton = new CustomButtonModel(
                btn,
                this.userDataService.applicationLanguage,
                this.customerDataService.customerData.DefaultApplicationLanguageType,
            );
            menuItems.push({
                ApplicationSettingType: undefined,
                labelText: customButton.GetCaption(),
                Icon: customButton.IconClass,
                buttonModel: customButton,
                onClick: (menuItem, $event) => {
                    $event.preventDefault();
                    this.openWindow(menuItem.buttonModel);
                },
                getUrl: customButton.Url ? () => customButton.Url : undefined,
            });
        });

        return menuItems;
    }

    private buildMenuItemsMap() {
        this.menuItemsMap = [
            {
                ApplicationSettingType: undefined,
                labelText: this.appTextService.get('common.home'),
                Icon: 'homeCcmIcon',
                State: FiStateNames.Home._name,
                count$: this.fiSignalRHelperService.newNewsFeeds$.pipe(map((ids) => ids.length)),
                CountState: FiStateNames.Home._name,
                CountParams: { isOnlyUnvisited: true },
                countMoreMenuSettings: {
                    menuItems: [
                        {
                            title: 'mark_all_as_read',
                            onClick: (menuItem, $event) => {
                                $event.preventDefault();
                                this.markAllNewsFeedsAsSeen();
                            },
                        },
                    ],
                },
            },
            {
                ApplicationSettingType: [
                    ApplicationSettingsType.DocumentLocal,
                    ApplicationSettingsType.DocumentCentral,
                ],
                labelText: this.appTextService.get('common.pages'),
                Icon: 'docCcmIcon',
                State: FiStateNames.Document.List,
                RootStates: [FiStateNames.Document._name],
                ModuleType: ModuleType.Document,
                count$: this.fiSignalRHelperService.newDocumentIds$.pipe(map((ids) => ids.length)),
                CountState: FiStateNames.Document.List,
                CountParams: {
                    sortColumn: 'ChangedOn',
                    sortDirection: true,
                },
                countMoreMenuSettings: {
                    menuItems: [
                        {
                            title: 'mark_all_as_read',
                            onClick: (menuItem, $event) => {
                                $event.preventDefault();
                                this.markAllDocumentsAsSeen();
                            },
                        },
                    ],
                },
            },
            {
                ApplicationSettingType: [
                    ApplicationSettingsType.MediaBankLocal,
                    ApplicationSettingsType.MediaBankCentral,
                ],
                labelText: this.appTextService.get('common.files'),
                Icon: 'pictureCcmIcon',
                State: FiStateNames.MediaBank.List,
                RootStates: ['Module.MediaBank.'],
                ModuleType: ModuleType.MediaBank,
                count$: this.fiSignalRHelperService.newMediaFileIds$.pipe(map((ids) => ids.length)),
                CountState: FiStateNames.MediaBank.List,
                CountParams: {
                    filterType: SideListOptionType.New,
                    sortColumn: 'ChangedOn',
                    sortDirection: true,
                },
                countMoreMenuSettings: {
                    menuItems: [
                        {
                            title: 'mark_all_as_read',
                            onClick: (menuItem, $event) => {
                                $event.preventDefault();
                                this.markAllMediaFilesAsSeen();
                            },
                        },
                    ],
                },
            },
            {
                ApplicationSettingType: [ApplicationSettingsType.FormLocal],
                //ExcludingSettingTypes:[], // todo FormReports?
                labelText: this.appTextService.get('form.module_name'),
                Icon: 'fa fa-clipboard-list',
                State: FiStateNames.Form.List,
                StateParams: {
                    filterType: SideListOptionType.FormsToAnswer,
                },
                count$: this.formService.newItemsCount$,
                RootStates: [FiStateNames.Form._name],
            },
            {
                ApplicationSettingType: [ApplicationSettingsType.Task],
                labelText: this.appTextService.get('task.tasks'),
                Icon: 'fa fa-clipboard-check',
                State: FiStateNames.Task.ToComplete,
                StateParams: {
                    tabNumber: FiTaskToCompleteTabsEnum.AssignedToMe,
                },
                RootStates: [FiStateNames.Task._name],
                count$: this.taskSyncService.tasksToBeDoneCount$,
            },
            {
                ApplicationSettingType: [ApplicationSettingsType.TaskV2Local],
                labelText: this.appTextService.get('task.tasks'),
                Icon: 'fa-regular fa-clipboard-check',
                RootStates: [FiStateNames.TaskV2._name],
                onClick: (menuItem, $event) => {
                    $event.preventDefault();
                    const tabs = this.fiTaskV2HelperService.getTaskV2SettingsTabs();
                    if (tabs.length)
                        this.stateService.go(tabs[0].Sref, {
                            filterType: SideListOptionType.TasksAssignedToMe,
                        });
                },
                count$: this.taskSyncService.tasksToBeDoneCount$,
            },
            {
                ApplicationSettingType: [ApplicationSettingsType.Forum],
                labelText: this.appTextService.get('forum.module_name'),
                Icon: 'fa fa-comment',
                State: FiStateNames.Forum.List,
                StateParams: {
                    filterType: SideListOptionType.ForumSharedToMe,
                },
                RootStates: [FiStateNames.Forum._name],
                ModuleType: ModuleType.ForumPost,
                //isHidden: () => !this.customerDataService.isTestCustomer(),
                count$: this.fiSignalRHelperService.newForumPostIds$.pipe(map((ids) => ids.length)),
                CountState: FiStateNames.Forum.List,
                CountParams: {
                    filterType: SideListOptionType.ForumNewPosts,
                },
            },
            {
                ApplicationSettingType: [ApplicationSettingsType.BalooELearning],
                labelText: this.appTextService.get('baloo.baloo'),
                Icon: 'fa-regular fa-file-certificate',
                getUrl: () => {
                    return '/';
                },
                onClick: (menuItem, $event) => {
                    this.balooService.getBalooMagicLink().then((url) => {
                        window.open(url, '_blank');
                    });
                    $event.preventDefault();
                },
            },
            {
                ApplicationSettingType: [ApplicationSettingsType.Email],
                labelText: this.appTextService.get('common.email'),
                Icon: 'mailCcmIcon',
                getUrl: () => {
                    return this.userDataService.userData.WebmailAccountsCount > 0
                        ? this.fiBackend.getRequestUrl('WebMail/Index', {
                              addTokenToUrl: true,
                          })
                        : '/';
                },
                onClick: (menuItem, $event) => {
                    if (!(this.userDataService.userData.WebmailAccountsCount > 0)) {
                        $event.preventDefault();
                        this.fiDialogsService.notify({
                            messageText: this.appTextService.get('email.add_email_warning'),
                            headerText: this.appTextService.get('common.warning'),
                        });
                    }
                },
            },
            {
                ApplicationSettingType: [
                    ApplicationSettingsType.CoreLocal,
                    ApplicationSettingsType.CoreCentral,
                    ApplicationSettingsType.CoreGlobal,
                ],
                labelText: this.appTextService.get('common.our_organisation'),
                Icon: 'userCcmIcon',
                State: FiStateNames.User.List,
                RootStates: [FiStateNames.Core._name],
            },
            {
                ApplicationSettingType: [
                    ApplicationSettingsType.Tag,
                    ApplicationSettingsType.ConfidentialAgreement,
                    ApplicationSettingsType.CoreCentral,
                    ApplicationSettingsType.Appearance,
                    ApplicationSettingsType.BillingLocal,
                    ApplicationSettingsType.Personalkollen,
                    ApplicationSettingsType.Planday,
                ],
                labelText: this.appTextService.get('module.system_setup'),
                Icon: 'cogsCcmIcon',
                onClick: (menuItem, $event) => {
                    $event.preventDefault();
                    const globalSettingsTabs = this.globalSettingsService.getGlobalSettingsTabs();
                    if (globalSettingsTabs.length) this.stateService.go(globalSettingsTabs[0].Sref);
                },
                getUrl: () => {
                    const globalSettingsTabs = this.globalSettingsService.getGlobalSettingsTabs();
                    if (globalSettingsTabs.length)
                        return this.stateService.href(globalSettingsTabs[0].Sref, globalSettingsTabs[0].SrefParams);
                    return '/';
                },
                isHidden: () => {
                    const globalSettingsTabs = this.globalSettingsService.getGlobalSettingsTabs();
                    return !globalSettingsTabs.length;
                },
                RootStates: [
                    FiStateNames.Tag._name,
                    FiStateNames.SiteMap._name,
                    FiStateNames.Agreement._name,
                    FiStateNames.History._name,
                    FiStateNames.Appearance._name,
                    FiStateNames.OtherSettings._name,
                    FiStateNames.Import._name,
                    FiStateNames.Role._name,
                    FiStateNames.BusinessArea._name,
                    FiStateNames.ExtendedRoleRight._name,
                    FiStateNames.Billing._name,
                ],
            },
            {
                ApplicationSettingType: [ApplicationSettingsType.ADIntegration],
                labelText: this.appTextService.get('ad_integration.module_name'),
                Icon: 'fa fa-users',
                State: 'ADIntegration.GroupMapping',
                RootStates: ['ADIntegration.'],
            },
            {
                ApplicationSettingType: [ApplicationSettingsType.CustomerBilling],
                labelText: this.appTextService.get('billing.customer_billing'),
                Icon: 'customerbillingCcmIcon',
                State: FiStateNames.CustomerBilling.Customers.List,
                RootStates: [FiStateNames.CustomerBilling._name],
            },
            {
                ApplicationSettingType: [ApplicationSettingsType.Statistics2],
                labelText: this.appTextService.get('common.statistics'),
                Icon: 'chartBarsCcmIcon',
                State: FiStateNames.Statistic2.Dashboard,
                StateParams: { reloadData: true },
                RootStates: [FiStateNames.Statistic2.Dashboard, FiStateNames.Statistic2.Report._name],
            },
            {
                ApplicationSettingType: [ApplicationSettingsType.ChecklistLocal],
                labelText: this.appTextService.get('checklist.module_name'),
                Icon: 'fa fa-check-square-o',
                onClick: (menuItem, $event) => {
                    $event.preventDefault();
                    const tabs = this.fiChecklistStatesService.getChecklistSettingsTabs();
                    if (tabs.length)
                        this.stateService.go(tabs[0].Sref, {
                            filterType: SideListOptionType.CurrentCheckLists,
                        });
                },
                RootStates: [FiStateNames.Checklist._name],
                count$: this.checklistsReminderService.checkListRemindersCount$.asObservable(),
                CountState: FiStateNames.Checklist.List,
                CountParams: {
                    filterType: SideListOptionType.CurrentCheckLists,
                },
            },
            {
                ApplicationSettingType: [ApplicationSettingsType.ChecklistV2Local],
                labelText: this.appTextService.get('checklist.checklists_v2'),
                Icon: 'fa-solid fa-ballot-check',
                onClick: (menuItem, $event) => {
                    $event.preventDefault();
                    const tabs = this.checklistV2StatesService.getChecklistV2SettingsTabs();
                    if (tabs.length)
                        this.stateService.go(tabs[0].Sref, {
                            filterType: SideListOptionType.CurrentCheckLists,
                        });
                },
                count$: this.fiChecklist2SyncService.notCompletedChecklistsV2Count$,
                RootStates: [FiStateNames.ChecklistV2._name],
                //isHidden: () => !this.customerDataService.isTestCustomer(),
            },
            {
                ApplicationSettingType: [ApplicationSettingsType.ChecklistV2AuditLocal],
                labelText: this.appTextService.get('checklist.checklists_audit_v2'),
                Icon: 'fas fa-person-chalkboard',
                onClick: (menuItem, $event) => {
                    $event.preventDefault();
                    const tabs = this.checklistV2StatesService.getChecklistV2AuditSettingsTabs();
                    if (tabs.length)
                        this.stateService.go(tabs[0].Sref, {
                            filterType: SideListOptionType.SharedWithMe,
                        });
                },
                count$: this.fiChecklist2SyncService.notCompletedChecklistV2AuditCount$,
                RootStates: [FiStateNames.ChecklistV2Audit._name],
            },
            {
                ApplicationSettingType: [ApplicationSettingsType.OnboardingLocal],
                labelText: this.appTextService.get('onboarding.onboarding'),
                Icon: 'fa-regular fa-file-certificate',
                RootStates: [FiStateNames.Onboarding._name],
                onClick: (menuItem, $event) => {
                    $event.preventDefault();
                    this.fiOnboardingNavigationService.onOnboardingMenuItemClick();
                },
                getUrl: () => {
                    const tabs = this.fiOnboardingNavigationService.getOnboardingTabs();
                    if (tabs.length) {
                        return this.stateService.href(tabs[0].Sref);
                    }
                    return '/';
                },
                count$: this.fiSignalRHelperService.activeOnboardingsCount$,
            },
            {
                ApplicationSettingType: [ApplicationSettingsType.CustomFranchiseIntranetUpgrade],
                labelText: this.appTextService.get('upgrade.upgrades'),
                Icon: 'arrows-cwCcmIcon',
                State: FiStateNames.Upgrade.List,
                RootStates: [
                    FiStateNames.Upgrade._name,
                    FiStateNames.Upgrade.View,
                    FiStateNames.Upgrade.Edit,
                    FiStateNames.Upgrade.Add,
                ],
            },
            {
                ApplicationSettingType: [ApplicationSettingsType.SMS],
                labelText: this.appTextService.get('sms_module_name'),
                Icon: 'fa fa-mobile sms-module',
                State: FiStateNames.SMS.List,
                RootStates: [FiStateNames.SMS._name],
            },
            {
                ApplicationSettingType: [ApplicationSettingsType.CoreLocal],
                labelText: this.appTextService.get('translation.translations'),
                Icon: 'fa fa-font',
                State: FiStateNames.Translation.List,
                RootStates: [FiStateNames.Translation._name],
                isHidden: () => !this.customerDataService.isTestCustomer(),
            },
            /*
                        {
                            ApplicationSettingType: [ApplicationSettingsType.BillingLocal, ApplicationSettingsType.BillingCentral],
                            labelText: this.appTextService.get('billing.billing'),
                            Icon: "billingCcmIcon",
                            State: FiStateNames.Billing.Central.List,
                            RootStates: [FiStateNames.Billing._name]
                        }
            */
        ];
    }

    private markAllNewsFeedsAsSeen() {
        if (this.fiSignalRHelperService.getNewNewsFeedsCount()) {
            this.newsFeedSharedService.markAllNewsFeedsAsSeen();
        }
    }

    private markAllDocumentsAsSeen() {
        if (this.fiSignalRHelperService.getNewDocumentIdsCount()) {
            this.documentService.markAllDocumentsAsSeen().then(() => {
                this.fiSignalRHelperService.clearNewDocumentIds();
            });
        }
    }

    private markAllMediaFilesAsSeen() {
        if (this.fiSignalRHelperService.getNewMediaFileIdsCount()) {
            this.mediaBankService.markAllMediaBankFilesAsSeen().then(() => {
                this.fiSignalRHelperService.clearNewMediaFileIds();
            });
        }
    }

    // note: not used ?
    private postAction(hostWindow, buttonAction) {
        const form = hostWindow.document.createElement('form');
        //form.target = "_blank";
        form.method = 'POST';
        form.action = buttonAction.Url;
        //form.style.display = "none";

        buttonAction.Parameters.forEach((parameter) => {
            const input = hostWindow.document.createElement('input');
            input.type = 'hidden';
            input.name = parameter.Key;
            input.value = parameter.Value;
            form.appendChild(input);
        });

        hostWindow.document.body.appendChild(form);
        form.submit();
        //document.body.removeChild(form);
    }

    private openWindow(buttonModel: CustomButtonModel) {
        let url = buttonModel.Url;
        if (!url) url = this.fiBackend.getRequestUrl('CustomButtonHandler/' + buttonModel.Id, { addTokenToUrl: true });

        if (buttonModel.OpenInSameWindow) window.location.href = url;
        else window.open(url, '_blank');
    }
}
